import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useTheme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { MarketLink } from '@market/react';
import Info from '@square-icons/react/16/UI/circle-info-fill';
import { displayVAT } from 'utilities/currency';
import { SHIPPING_QUOTE_TYPES } from 'constants/enums';
import clsx from 'clsx';
import ProductCompatibleSoftwareSection from 'components/pages/product/product_compatible_software_section';
import { getCountryCode } from 'utilities/locales';
import IssuerInstallmentsHeadingPrice from './issuer_installments_heading_price';

const STOCK_STATUSES = {
  OUT_OF_STOCK: 'out_of_stock',
  BACKORDERED: 'backordered',
  EXTENDED: 'extended',
  PREORDER: 'preorder',
  IN_STOCK: 'in_stock',
  NO_SHIP: 'no_ship',
};

const useStyles = makeStyles((theme) => ({
  stockColor: ({ stockStatus }) => ({
    color: (() => {
      switch (stockStatus) {
        case STOCK_STATUSES.OUT_OF_STOCK:
        case STOCK_STATUSES.BACKORDERED: {
          return theme.color.red;
        }
        case STOCK_STATUSES.EXTENDED:
        case STOCK_STATUSES.PREORDER: {
          return '#ED770B';
        }
        case STOCK_STATUSES.IN_STOCK:
        case STOCK_STATUSES.NO_SHIP:
        default: {
          return theme.color.green;
        }
      }
    })(),
  }),
  financingModalIcon: {
    fill: theme.color.blue,
    marginLeft: theme.spacing(0.5),
    verticalAlign: 'middle',
  },
}));

const ProductHeading = (props) => {
  const {
    currentProduct: {
      compatible_software,
      default_price,
      display_strikethrough,
      financing,
      issuer_installments,
      name,
      sale_price,
      sale_price_unformatted,
      price_with_vat,
      shipping,
      subtitle,
    },
    backorderedHeading,
    onFinancingTriggerClick = () => {},
    showStartingAt = false,
    customPrice,
  } = props;

  const isFree = () => sale_price_unformatted === 0;
  const getStockStatus = () => {
    if (backorderedHeading) {
      return STOCK_STATUSES.BACKORDERED;
    } else if (shipping.no_ship) {
      return STOCK_STATUSES.NO_SHIP;
    } else if (shipping.out_of_stock) {
      return STOCK_STATUSES.OUT_OF_STOCK;
    } else if (shipping.ship_quote.sq_type === SHIPPING_QUOTE_TYPES.EXTENDED) {
      return STOCK_STATUSES.EXTENDED;
    } else if (shipping.ship_quote.sq_type === SHIPPING_QUOTE_TYPES.PREORDER) {
      return STOCK_STATUSES.PREORDER;
    } else {
      return STOCK_STATUSES.IN_STOCK;
    }
  };

  const classes = useStyles({
    stockStatus: getStockStatus(),
  });
  const { t } = useTranslation();
  const theme = useTheme();

  const displayVATFinancingCTA = () => {
    const noVATFinancingCTA = ['ES', 'FR'];
    return (
      displayVAT() &&
      !noVATFinancingCTA.includes(getCountryCode().toUpperCase())
    );
  };

  const plusVAT = (testId) => (
    <span data-testid={testId} className="medium-10">
      {` ${t('common.plusVAT')}`}
    </span>
  );

  const inclVAT = () => (
    <Box
      fontSize={theme.type.fontSize.base}
      color={theme.color.gray['55']}
      data-testid="heading-incl-vat"
      mt={1}
    >
      <span>{price_with_vat}</span>
      <span className="medium-10">{` ${t('common.inclVAT')}`}</span>
    </Box>
  );

  const stockInfoText = () => {
    const stockStatus = getStockStatus();
    switch (stockStatus) {
      case STOCK_STATUSES.BACKORDERED: {
        return backorderedHeading;
      }
      case STOCK_STATUSES.OUT_OF_STOCK: {
        return t('productPage.outOfStock');
      }
      case STOCK_STATUSES.PREORDER:
      case STOCK_STATUSES.EXTENDED: {
        const quote = shipping.ship_quote;
        const extendedEstimate = t('productPage.extendedShippingEstimate', {
          verb: t(`shipping.shippingEstimate.verbs.${quote.verb}`),
          shippingEstimate: t('productPage.shippingEstimate', {
            min: quote.min,
            max: quote.max,
            timeUnit: t(
              `shipping.shippingEstimate.timeUnits.${quote.time_unit}.other`,
            ),
          }),
        });

        return stockStatus === STOCK_STATUSES.EXTENDED
          ? `${t('productPage.backordered')} · ${extendedEstimate}`
          : extendedEstimate;
      }
      case STOCK_STATUSES.NO_SHIP: {
        return t('productPage.available');
      }
      case STOCK_STATUSES.IN_STOCK:
      default: {
        return `${t('productPage.inStock')} · ${t('productPage.freeShipping')}`;
      }
    }
  };

  const renderFinancingModalTrigger = () => (
    <MarketLink
      onClick={onFinancingTriggerClick}
      className="medium-10"
      style={{ marginLeft: theme.spacing(0.5), verticalAlign: 'middle' }}
    >
      <span>
        {t('productPage.financingModalTrigger', {
          price: financing.monthly_financing_charge,
          period: financing.number_payment_periods,
        })}
        {displayVATFinancingCTA() && plusVAT('heading-financing-vat')}
        <Info
          style={{
            fill: theme.color.blue,
            marginLeft: theme.spacing(0.5),
            verticalAlign: 'middle',
          }}
        />
      </span>
    </MarketLink>
  );

  return (
    <div>
      <p className={clsx('medium-20', classes.stockColor)}>{stockInfoText()}</p>
      <Box component="h1" sx={{ my: 2 }}>
        {name}
      </Box>
      {customPrice ? (
        <Box fontSize={theme.type.fontSize.large} my={2}>
          {customPrice}
        </Box>
      ) : (
        <Box fontSize={theme.type.fontSize.large} my={2}>
          {showStartingAt && (
            <Box component="span" mr={1}>
              {t('productPage.startingAt')}
            </Box>
          )}
          {display_strikethrough && (
            <Box component="span" mr={1}>
              <s>{default_price}</s>
            </Box>
          )}
          <span>{sale_price}</span>
          {displayVAT() && !isFree() && plusVAT('heading-plus-vat')}
          {financing.monthly_financing_charge && renderFinancingModalTrigger()}
          {!financing.monthly_financing_charge && (
            <IssuerInstallmentsHeadingPrice
              issuerInstallmentsDetails={issuer_installments}
            />
          )}
          {displayVAT() && !isFree() && inclVAT()}
        </Box>
      )}
      {subtitle && <p data-testid="subtitle">{subtitle}</p>}
      {compatible_software && (
        <Box mt={1} mb={2}>
          <ProductCompatibleSoftwareSection software={compatible_software} />
        </Box>
      )}
    </div>
  );
};

export default ProductHeading;
